'use client';
import React, { useState, FormEvent } from 'react';
import { consultationService } from '../../services/consultationService';

interface ConsultationBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingType: 'book_now' | 'strategic_consultation';
  title?: string;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  message?: string;
}

interface TouchedFields {
  fullName: boolean;
  email: boolean;
  phone: boolean;
  companyName: boolean;
  message: boolean;
}

export default function ConsultationBookingModal({
  isOpen,
  onClose,
  bookingType,
  title,
}: ConsultationBookingModalProps) {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touchedFields, setTouchedFields] = useState<TouchedFields>({
    fullName: false,
    email: false,
    phone: false,
    companyName: false,
    message: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Validation functions
  const validateFullName = (value: string): string | undefined => {
    if (!value.trim()) {
      return 'Full name is required';
    }
    if (value.trim().length < 2) {
      return 'Full name must be at least 2 characters';
    }
    if (!/^[a-zA-Z\s'-]+$/.test(value)) {
      return 'Full name can only contain letters, spaces, hyphens, and apostrophes';
    }
    return undefined;
  };

  const validateEmail = (value: string): string | undefined => {
    if (!value.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return 'Please enter a valid email address';
    }
    return undefined;
  };

  const validatePhone = (value: string): string | undefined => {
    if (!value.trim()) {
      return undefined; // Phone is optional
    }
    const phoneRegex = /^[\d\s+()-]+$/;
    if (!phoneRegex.test(value)) {
      return 'Please enter a valid phone number';
    }
    if (value.replace(/\D/g, '').length < 10) {
      return 'Phone number must be at least 10 digits';
    }
    return undefined;
  };

  const validateCompanyName = (value: string): string | undefined => {
    if (!value.trim()) {
      return undefined; // Company name is optional
    }
    if (value.trim().length < 2) {
      return 'Company name must be at least 2 characters';
    }
    return undefined;
  };

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'fullName':
        return validateFullName(value);
      case 'email':
        return validateEmail(value);
      case 'phone':
        return validatePhone(value);
      case 'companyName':
        return validateCompanyName(value);
      default:
        return undefined;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Real-time validation as user types (only if field has been touched)
    if (touchedFields[name as keyof TouchedFields]) {
      const error = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Mark field as touched
    setTouchedFields((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Validate on blur
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const validateAllFields = (): boolean => {
    const newErrors: FormErrors = {
      fullName: validateFullName(formData.fullName),
      email: validateEmail(formData.email),
      phone: validatePhone(formData.phone),
      companyName: validateCompanyName(formData.companyName),
    };

    setErrors(newErrors);
    
    // Mark all fields as touched
    setTouchedFields({
      fullName: true,
      email: true,
      phone: true,
      companyName: true,
      message: true,
    });

    // Check if there are any errors
    return !Object.values(newErrors).some((error) => error !== undefined);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate all fields before submission
    if (!validateAllFields()) {
      setSubmitMessage({ 
        type: 'error', 
        text: 'Please fix the errors in the form before submitting.' 
      });
      return;
    }

    setLoading(true);
    setSubmitMessage(null);

    try {
      const { data, error } = await consultationService.createBooking({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone || undefined,
        companyName: formData.companyName || undefined,
        message: formData.message || undefined,
        bookingType,
      });

      if (error) {
        setSubmitMessage({ type: 'error', text: error.message });
      } else if (data) {
        setSubmitMessage({
          type: 'success',
          text: 'Your consultation booking has been submitted successfully! We will contact you shortly.',
        });
        
        // Reset form after 2 seconds and close modal
        setTimeout(() => {
          setFormData({ fullName: '', email: '', phone: '', companyName: '', message: '' });
          setErrors({});
          setTouchedFields({
            fullName: false,
            email: false,
            phone: false,
            companyName: false,
            message: false,
          });
          setSubmitMessage(null);
          onClose();
        }, 2000);
      }
    } catch (err) {
      setSubmitMessage({ type: 'error', text: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getInputClassName = (fieldName: keyof FormErrors) => {
    const baseClasses = 'w-full px-3 py-2 border rounded-md focus:ring-2 focus:outline-none disabled:bg-gray-100 transition-colors';
    const hasError = touchedFields[fieldName] && errors[fieldName];
    
    if (hasError) {
      return `${baseClasses} border-red-500 focus:ring-red-500 focus:border-red-500`;
    }
    
    const hasValue = formData[fieldName]?.trim();
    if (touchedFields[fieldName] && hasValue && !errors[fieldName]) {
      return `${baseClasses} border-green-500 focus:ring-green-500 focus:border-green-500`;
    }
    
    return `${baseClasses} border-gray-300 focus:ring-blue-500 focus:border-transparent`;
  };

  if (!isOpen) return null;

  return (
    <div data-component-id="src/components/common/ConsultationBookingModal.tsx:244:4" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="244" data-component-end-line="422" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-0%20z-50%20flex%20items-center%20justify-center%20bg-black%20bg-opacity-50%22%7D" className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div data-component-id="src/components/common/ConsultationBookingModal.tsx:245:6" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="245" data-component-end-line="421" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20rounded-lg%20shadow-xl%20w-full%20max-w-md%20mx-4%20max-h-%5B90vh%5D%20overflow-y-auto%22%7D" className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div data-component-id="src/components/common/ConsultationBookingModal.tsx:246:8" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="246" data-component-end-line="420" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%22%7D" className="p-6">
          <div data-component-id="src/components/common/ConsultationBookingModal.tsx:247:10" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="247" data-component-end-line="258" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-between%20items-center%20mb-4%22%7D" className="flex justify-between items-center mb-4">
            <h2 data-component-id="src/components/common/ConsultationBookingModal.tsx:248:12" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="248" data-component-end-line="250" data-component-file="ConsultationBookingModal.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-gray-900%22%7D" className="text-2xl font-bold text-gray-900">
              {title || (bookingType === 'strategic_consultation' ? 'Book Strategic Consultation' : 'Book Now')}
            </h2>
            <button data-component-id="src/components/common/ConsultationBookingModal.tsx:251:12" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="251" data-component-end-line="257" data-component-file="ConsultationBookingModal.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22text-gray-400%20hover%3Atext-gray-600%20text-2xl%22%2C%22textContent%22%3A%22%C3%97%22%7D"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={loading}
            >
              ×
            </button>
          </div>

          <form data-component-id="src/components/common/ConsultationBookingModal.tsx:260:10" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="260" data-component-end-line="419" data-component-file="ConsultationBookingModal.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22space-y-4%22%7D" onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div data-component-id="src/components/common/ConsultationBookingModal.tsx:261:12" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="261" data-component-end-line="288" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <label data-component-id="src/components/common/ConsultationBookingModal.tsx:262:14" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="262" data-component-end-line="264" data-component-file="ConsultationBookingModal.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-gray-700%20mb-1%22%2C%22textContent%22%3A%22Full%20Name%20*%22%7D" htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <div data-component-id="src/components/common/ConsultationBookingModal.tsx:265:14" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="265" data-component-end-line="281" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
                <input data-component-id="src/components/common/ConsultationBookingModal.tsx:266:16" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="266" data-component-end-line="277" data-component-file="ConsultationBookingModal.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22id%22%3A%22fullName%22%2C%22name%22%3A%22fullName%22%7D"
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                  disabled={loading}
                  className={getInputClassName('fullName')}
                  placeholder="Enter your full name"
                />
                {touchedFields.fullName && !errors.fullName && formData.fullName.trim() && (
                  <span data-component-id="src/components/common/ConsultationBookingModal.tsx:279:18" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="279" data-component-end-line="279" data-component-file="ConsultationBookingModal.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20right-3%20top-2.5%20text-green-500%22%2C%22textContent%22%3A%22%E2%9C%93%22%7D" className="absolute right-3 top-2.5 text-green-500">✓</span>
                )}
              </div>
              {touchedFields.fullName && errors.fullName && (
                <p data-component-id="src/components/common/ConsultationBookingModal.tsx:283:16" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="283" data-component-end-line="286" data-component-file="ConsultationBookingModal.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-600%20flex%20items-center%22%7D" className="mt-1 text-sm text-red-600 flex items-center">
                  <span data-component-id="src/components/common/ConsultationBookingModal.tsx:284:18" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="284" data-component-end-line="284" data-component-file="ConsultationBookingModal.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22mr-1%22%2C%22textContent%22%3A%22%E2%9A%A0%22%7D" className="mr-1">⚠</span>
                  {errors.fullName}
                </p>
              )}
            </div>

            <div data-component-id="src/components/common/ConsultationBookingModal.tsx:290:12" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="290" data-component-end-line="317" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <label data-component-id="src/components/common/ConsultationBookingModal.tsx:291:14" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="291" data-component-end-line="293" data-component-file="ConsultationBookingModal.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-gray-700%20mb-1%22%2C%22textContent%22%3A%22Email%20*%22%7D" htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <div data-component-id="src/components/common/ConsultationBookingModal.tsx:294:14" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="294" data-component-end-line="310" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
                <input data-component-id="src/components/common/ConsultationBookingModal.tsx:295:16" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="295" data-component-end-line="306" data-component-file="ConsultationBookingModal.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22email%22%2C%22id%22%3A%22email%22%2C%22name%22%3A%22email%22%7D"
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                  disabled={loading}
                  className={getInputClassName('email')}
                  placeholder="your.email@example.com"
                />
                {touchedFields.email && !errors.email && formData.email.trim() && (
                  <span data-component-id="src/components/common/ConsultationBookingModal.tsx:308:18" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="308" data-component-end-line="308" data-component-file="ConsultationBookingModal.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20right-3%20top-2.5%20text-green-500%22%2C%22textContent%22%3A%22%E2%9C%93%22%7D" className="absolute right-3 top-2.5 text-green-500">✓</span>
                )}
              </div>
              {touchedFields.email && errors.email && (
                <p data-component-id="src/components/common/ConsultationBookingModal.tsx:312:16" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="312" data-component-end-line="315" data-component-file="ConsultationBookingModal.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-600%20flex%20items-center%22%7D" className="mt-1 text-sm text-red-600 flex items-center">
                  <span data-component-id="src/components/common/ConsultationBookingModal.tsx:313:18" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="313" data-component-end-line="313" data-component-file="ConsultationBookingModal.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22mr-1%22%2C%22textContent%22%3A%22%E2%9A%A0%22%7D" className="mr-1">⚠</span>
                  {errors.email}
                </p>
              )}
            </div>

            <div data-component-id="src/components/common/ConsultationBookingModal.tsx:319:12" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="319" data-component-end-line="345" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <label data-component-id="src/components/common/ConsultationBookingModal.tsx:320:14" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="320" data-component-end-line="322" data-component-file="ConsultationBookingModal.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-gray-700%20mb-1%22%2C%22textContent%22%3A%22Phone%22%7D" htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span data-component-id="src/components/common/ConsultationBookingModal.tsx:321:22" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="321" data-component-end-line="321" data-component-file="ConsultationBookingModal.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-gray-500%20text-xs%22%2C%22textContent%22%3A%22(Optional)%22%7D" className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <div data-component-id="src/components/common/ConsultationBookingModal.tsx:323:14" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="323" data-component-end-line="338" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
                <input data-component-id="src/components/common/ConsultationBookingModal.tsx:324:16" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="324" data-component-end-line="334" data-component-file="ConsultationBookingModal.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22tel%22%2C%22id%22%3A%22phone%22%2C%22name%22%3A%22phone%22%7D"
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={loading}
                  className={getInputClassName('phone')}
                  placeholder="+1 (555) 123-4567"
                />
                {touchedFields.phone && !errors.phone && formData.phone.trim() && (
                  <span data-component-id="src/components/common/ConsultationBookingModal.tsx:336:18" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="336" data-component-end-line="336" data-component-file="ConsultationBookingModal.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20right-3%20top-2.5%20text-green-500%22%2C%22textContent%22%3A%22%E2%9C%93%22%7D" className="absolute right-3 top-2.5 text-green-500">✓</span>
                )}
              </div>
              {touchedFields.phone && errors.phone && (
                <p data-component-id="src/components/common/ConsultationBookingModal.tsx:340:16" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="340" data-component-end-line="343" data-component-file="ConsultationBookingModal.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-600%20flex%20items-center%22%7D" className="mt-1 text-sm text-red-600 flex items-center">
                  <span data-component-id="src/components/common/ConsultationBookingModal.tsx:341:18" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="341" data-component-end-line="341" data-component-file="ConsultationBookingModal.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22mr-1%22%2C%22textContent%22%3A%22%E2%9A%A0%22%7D" className="mr-1">⚠</span>
                  {errors.phone}
                </p>
              )}
            </div>

            <div data-component-id="src/components/common/ConsultationBookingModal.tsx:347:12" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="347" data-component-end-line="373" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <label data-component-id="src/components/common/ConsultationBookingModal.tsx:348:14" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="348" data-component-end-line="350" data-component-file="ConsultationBookingModal.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-gray-700%20mb-1%22%2C%22textContent%22%3A%22Company%20Name%22%7D" htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                Company Name <span data-component-id="src/components/common/ConsultationBookingModal.tsx:349:29" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="349" data-component-end-line="349" data-component-file="ConsultationBookingModal.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-gray-500%20text-xs%22%2C%22textContent%22%3A%22(Optional)%22%7D" className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <div data-component-id="src/components/common/ConsultationBookingModal.tsx:351:14" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="351" data-component-end-line="366" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
                <input data-component-id="src/components/common/ConsultationBookingModal.tsx:352:16" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="352" data-component-end-line="362" data-component-file="ConsultationBookingModal.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22id%22%3A%22companyName%22%2C%22name%22%3A%22companyName%22%7D"
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={loading}
                  className={getInputClassName('companyName')}
                  placeholder="Your company name"
                />
                {touchedFields.companyName && !errors.companyName && formData.companyName.trim() && (
                  <span data-component-id="src/components/common/ConsultationBookingModal.tsx:364:18" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="364" data-component-end-line="364" data-component-file="ConsultationBookingModal.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20right-3%20top-2.5%20text-green-500%22%2C%22textContent%22%3A%22%E2%9C%93%22%7D" className="absolute right-3 top-2.5 text-green-500">✓</span>
                )}
              </div>
              {touchedFields.companyName && errors.companyName && (
                <p data-component-id="src/components/common/ConsultationBookingModal.tsx:368:16" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="368" data-component-end-line="371" data-component-file="ConsultationBookingModal.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-600%20flex%20items-center%22%7D" className="mt-1 text-sm text-red-600 flex items-center">
                  <span data-component-id="src/components/common/ConsultationBookingModal.tsx:369:18" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="369" data-component-end-line="369" data-component-file="ConsultationBookingModal.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22mr-1%22%2C%22textContent%22%3A%22%E2%9A%A0%22%7D" className="mr-1">⚠</span>
                  {errors.companyName}
                </p>
              )}
            </div>

            <div data-component-id="src/components/common/ConsultationBookingModal.tsx:375:12" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="375" data-component-end-line="390" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <label data-component-id="src/components/common/ConsultationBookingModal.tsx:376:14" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="376" data-component-end-line="378" data-component-file="ConsultationBookingModal.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-gray-700%20mb-1%22%2C%22textContent%22%3A%22Message%22%7D" htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Message <span data-component-id="src/components/common/ConsultationBookingModal.tsx:377:24" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="377" data-component-end-line="377" data-component-file="ConsultationBookingModal.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-gray-500%20text-xs%22%2C%22textContent%22%3A%22(Optional)%22%7D" className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <textarea data-component-id="src/components/common/ConsultationBookingModal.tsx:379:14" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="379" data-component-end-line="389" data-component-file="ConsultationBookingModal.tsx" data-component-name="textarea" data-component-content="%7B%22elementName%22%3A%22textarea%22%2C%22id%22%3A%22message%22%2C%22name%22%3A%22message%22%2C%22className%22%3A%22w-full%20px-3%20py-2%20border%20border-gray-300%20rounded-md%20focus%3Aring-2%20focus%3Aring-blue-500%20focus%3Aborder-transparent%20disabled%3Abg-gray-100%22%7D"
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                onBlur={handleBlur}
                rows={4}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                placeholder="Tell us about your consultation needs..."
              />
            </div>

            {submitMessage && (
              <div data-component-id="src/components/common/ConsultationBookingModal.tsx:393:14" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="393" data-component-end-line="399" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={`p-3 rounded-md ${
                  submitMessage.type === 'success' ?'bg-green-100 text-green-800 border border-green-200' :'bg-red-100 text-red-800 border border-red-200'
                }`}
              >
                {submitMessage.text}
              </div>
            )}

            <div data-component-id="src/components/common/ConsultationBookingModal.tsx:402:12" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="402" data-component-end-line="418" data-component-file="ConsultationBookingModal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-3%20pt-2%22%7D" className="flex gap-3 pt-2">
              <button data-component-id="src/components/common/ConsultationBookingModal.tsx:403:14" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="403" data-component-end-line="410" data-component-file="ConsultationBookingModal.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22flex-1%20px-4%20py-2%20border%20border-gray-300%20text-gray-700%20rounded-md%20hover%3Abg-gray-50%20disabled%3Abg-gray-100%20disabled%3Acursor-not-allowed%20transition-colors%22%2C%22textContent%22%3A%22Cancel%22%7D"
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button data-component-id="src/components/common/ConsultationBookingModal.tsx:411:14" data-component-path="src/components/common/ConsultationBookingModal.tsx" data-component-line="411" data-component-end-line="417" data-component-file="ConsultationBookingModal.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22submit%22%2C%22className%22%3A%22flex-1%20px-4%20py-2%20bg-blue-600%20text-white%20rounded-md%20hover%3Abg-blue-700%20disabled%3Abg-gray-400%20disabled%3Acursor-not-allowed%20transition-colors%22%7D"
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Submitting...' : 'Submit Booking'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}